//import EventStream from "Models/EventStream";
import { EventStreamPhaser2 } from 'Phaser/GameObjects/CursorMobile';
import { IteratorExtensible } from 'Phaser/GameObjects/CursorMobile';
import Phaser from 'phaser';
import { ErrorFeedbackManager } from 'Phaser/GameObjects/ErrorFeedbackManager';
import {
  AppTourStepPhaser,
  ReactToPhaserListeners,
  NotifyReactEvents,
  RemoveListener,
} from 'Phaser/EventBus2';
import type { MidiOnPayload } from 'Phaser/EventBus2';
import CursorMobile, { MOBILE_CURSOR_SCROLL_PADDING_LEFT } from 'Phaser/GameObjects/CursorMobile';
import IPlayable from 'Phaser/GameObjects/Playable';
import { PhraseGraphic } from 'Phaser/GameObjects/StaffLine';
import { NoteGraphic } from 'Phaser/GameObjects/StaffLine/Note';
import { INoteGraphicsDatabase } from './StaffExercise';
import { ProgressState } from 'Phaser/GameObjects/Progress';
import Phrase from 'Models/Phrase';
import ITimeKeeper from 'Models/ITimeKeeper';
import TimeKeeper from 'Models/TimeKeeper';
import { createTimeKeeper } from 'Models/createTimeKeeper';
import * as Tone from 'tone';
import Logger from 'Utils/Logger';
import { reportError } from 'Utils/reportError';
import { NoteType } from '@museflow-ai/opensheetmusicdisplay';
import TimeSignature from 'Models/TimeSignature';
import ChevronManager from 'Phaser/Gameplay/ChevronManager';
import { COLORBLIND_TYPE, isCapacitorNative } from 'Utils/Constants';
import MuseFlowAudio from 'Utils/MuseFlowAudioPlugin';
import { AdaptiveTempoController } from 'Phaser/Gameplay/AdaptiveTempoController';
import { midiNumberFromOsmdPitch, noteFromOsmdPitch } from 'Phaser/Util/OSMDHelper';
import { getScoreThemePalette, normalizeScoreTheme } from 'Utils/ScoreTheme';
import type { ScoreTheme } from 'Utils/ScoreTheme';
import type { ParallelModeConversion } from 'Utils/MusicXmlModeConversion';
import type { FingeringDisplay, LiveFingering } from 'Fingering/display';
import {
  PhraseLayout,
  PhraseManagerMuseflowRenderer,
  type RequestParams,
} from 'Phaser/Gameplay/PhraseManager';
import { CameraController } from 'Phaser/Gameplay/CameraController';
import { notifyAppAudioOutput } from 'AudioRecognition/appAudioSuppression';
import {
  getEventInputLatencyMs,
  getLatencyAdjustedMeasureTimestamp,
} from 'Utils/AudioInputLatency';

const AUTOSTART = false;

// limit the value to the nearest step (0.05)
function _limitValueToStep(value: number, step: number) {
  return Math.floor(value / step) * step;
}

export class PlayablePhrase {
  cursor?: CursorMobile;
  graphic: PhraseGraphic;
  idx: number = 0;
  isActive: boolean = false;

  constructor(graphic: PhraseGraphic, cursor?: CursorMobile) {
    this.cursor = cursor;
    this.graphic = graphic;
  }

  activate(unpause?: boolean, opaque = true) {
    this.isActive = true;
    this.cursor?.enable(opaque);
    //this.cursor?.setAlpha();
    if (unpause) this.cursor?.unpause();
  }

  deactivate() {
    this.isActive = false;
    this.cursor?.disable();
  }

  pause() {
    this.cursor?.pause();
  }

  update() {
    this.graphic.update();
    if (this.isActive) this.cursor?.update();
  }

  destroy() {
    console.debug(
      `[phrase-destroy] PlayablePhrase.destroy uuid=${this.graphic._data.uuid} startTs=${this.graphic._data.startTimestamp} isActive=${this.isActive}`,
    );
    this.graphic.destroy();
    this.cursor?.destroy();
  }
}

export default class SightReadingPhaser extends Phaser.Scene implements INoteGraphicsDatabase {
  /*phrases: (PlayablePhrase | undefined)[] = [];
  mainIterator!: IteratorExtensible;
  //cursors: CursorMobile[] = [];
  currentCursor: number = 0;
  currentIdx?: number;
  totalPhrases: number = 0;*/
  phraseManager!: PhraseManagerMuseflowRenderer;
  eventStream?: EventStreamPhaser2;
  log?: Phaser.GameObjects.Text;

  timeKeeperAuxiliary?: ITimeKeeper;

  clickAudio?: Phaser.Sound.BaseSound;
  tempo: number = 72;

  lastTickTime?: number;
  lastMeasure?: number;

  pauseQueued = false;
  private _playbackStarted = false;
  shouldUpdate = false;

  // Mirrored into the manager so its onResize knows whether to snap the cursor
  // to the first note (only before playback starts).
  get playbackStarted(): boolean {
    return this._playbackStarted;
  }
  set playbackStarted(v: boolean) {
    this._playbackStarted = v;
    if (this.phraseManager) this.phraseManager.playbackStarted = v;
  }

  measureCount: number = 0;

  mobileView: boolean = false;
  width: number = 0;
  height: number = 0;

  private cameraController!: CameraController;
  originMarker: any;

  // Phrase storage, the active-phrase index and the shared mobile cursor now
  // live on the PhraseManager; these accessors delegate so the rest of the
  // scene can keep its existing call sites. `phrases` stays public because
  // CursorMobile reads it for error-feedback positioning.
  get phrases() {
    return this.phraseManager.phrases;
  }

  private get activePhrase(): number {
    return this.phraseManager.activePhrase ?? 0;
  }
  private set activePhrase(v: number) {
    this.phraseManager.activePhrase = v;
    this.phraseManager.notifyActiveKey();
  }

  private get mobileCursor(): CursorMobile | undefined {
    return this.phraseManager.mainCursor;
  }
  private set mobileCursor(c: CursorMobile | undefined) {
    this.phraseManager.mainCursor = c;
  }

  /** The visible top-stave phrase (used by the app tour); falls back to the
   *  active/first phrase. */
  private getTopPhrase() {
    return (
      this.phrases.find((p) => p && !p.graphic.bottom) ??
      this.phraseManager.getActivePhrase() ??
      this.phraseManager.getFirstPhrase()
    );
  }

  getActivePhrase(): number {
    return this.activePhrase;
  }
  private phrasesTillNextTier = 4;
  private freePlayMode: boolean = false;

  private gameplayStarted: boolean = false;

  private eventsQueue: MidiOnPayload[] = [];
  private startPlayingTriggerEventTimestamp?: number;

  private nextPhraseStartTimer?: Phaser.Time.TimerEvent;

  private chevronManager?: ChevronManager;
  private errorFeedbackManager?: ErrorFeedbackManager;

  private zoomLevel: number = 1;
  private cursorHidden: boolean = false;

  /** App tour: current step (null when not in tour). */
  private appTourStep: AppTourStepPhaser | null = null;
  /** App tour: true while playback demo (cursor + metronome) is running. */
  private appTourPlaybackActive: boolean = false;
  /** App tour: snapshot to restore on EndTour. */
  private savedTourState?: {
    activePhrase: number;
    timeKeeperPaused: boolean;
    lastTickTime: number | undefined;
  };
  /** App tour: demo timekeeper for playback step (72 BPM). */
  private demoTimeKeeper?: TimeKeeper;
  /** App tour: demo iterator reference (for EndReached check). */
  private appTourDemoIterator?: IteratorExtensible;
  /** App tour: note IDs in order by slice (notes only, no rests) for coloring steps. */
  private appTourNoteIdsBySlice: string[] = [];
  /** App tour: current coloring step index (0=Perfect, 1=OffTime, 2=Error). */
  private appTourColoringStepIndex: number = 0;
  /** App tour: note IDs that had setDemoTimingState called (for restore on EndTour). */

  private appTourDemoTouchedNoteIds: Set<string> = new Set();
  /** Phrase raw (Phrase) by uuid for building demo iterator. */
  private phraseRawByUuid: Map<string, Phrase> = new Map();

  private isTourActive: boolean = false;
  private savedCursorPosition?: number;

  private notePosGizmo?: Phaser.GameObjects.Rectangle;

  private adaptiveTempoController?: AdaptiveTempoController;
  private scoreTheme: ScoreTheme = 'light';

  private nextAdaptiveSegment?: {
    base: number;
    delta: number;
    startTimestamp: number;
    endTimestamp: number;
  };

  public debugText?: Phaser.GameObjects.Text;

  constructor() {
    super('SightReadingPhaser');
  }

  create() {
    NotifyReactEvents.CurrentSceneReady(this);
    this.events.once('shutdown', this.destroy, this);
  }

  debugLog(text: string) {
    if (process.env.REACT_APP_NODE_ENV === 'prd' || process.env.REACT_APP_NODE_ENV === 'stg')
      return;
    //Logger.debug(text);
    this.debugText?.setText(text);
  }

  init({
    eventStream,
    mobile,
    width,
    height,
    zoomLevel,
    goalTempo = 72,
    ataEnabled = false,
    freePlayMode = false,
    practiceDarkMode = false,
    scoreTheme,
    transposeSemitones = 0,
    modeConversion = 'off',
    cursorVisible = true,
    playSessionId,
    fingering,
  }: {
    eventStream: EventStreamPhaser2;
    mobile?: boolean;
    width: number;
    height: number;
    zoomLevel: number;
    goalTempo?: number;
    ataEnabled?: boolean;
    freePlayMode?: boolean;
    practiceDarkMode?: boolean;
    scoreTheme?: ScoreTheme;
    transposeSemitones?: number;
    modeConversion?: ParallelModeConversion;
    cursorVisible?: boolean;
    /** MUS-495: the session the scene is booting for, absent when it boots
     *  ahead of session creation. */
    playSessionId?: string | null;
    fingering?: LiveFingering;
  }) {
    this.scoreTheme = normalizeScoreTheme(scoreTheme, practiceDarkMode);
    // Seed cursor visibility before any phrases are requested so their cursors
    // render correctly (hidden when off) from the first frame.
    this.cursorHidden = !cursorVisible;
    // Debug overlay disabled — re-enable if needed for diagnostics.
    // this.debugText =
    //   process.env.REACT_APP_NODE_ENV !== 'prd' && process.env.REACT_APP_NODE_ENV !== 'stg'
    //     ? this.add
    //         .text(100, 100, 'DebugLog', { fontSize: '32px', color: '#000000' })
    //         .setScrollFactor(0)
    //     : undefined;
    this.debugText = undefined;
    const layoutConfig = {
      layoutDirection: mobile ? PhraseLayout.Horizontal : PhraseLayout.Vertical,
      minPaddingX: 20,
      minPaddingY: 10,
    };
    const onPhraseRequested = this.onPhraseRequested;
    const palette = getScoreThemePalette(this.scoreTheme);
    const phraseManagerConfig = {
      mobile,
      maxPhrasesVisible: mobile ? 5 : 2,
      onPhraseRequested,
      layoutConfig,
      eventStream,
      // Gameplay logic stays on the scene; the manager forwards each cursor's
      // callbacks here.
      onPlayingLastSlice: this.onPlayingLastSlice.bind(this),
      onFinishPlayingPhrase: this.onFinishPlayingPhrase.bind(this),
      onStartPlayingMeasure: this.onStartPlayingMeasure.bind(this),
      onPhraseRemoved: (uuid: string) => this.errorFeedbackManager?.cleanupPhrase(uuid),
      // Score-theme colors applied to phrases/cursors the manager creates.
      notationColor: palette.notationColorNumber,
      staffLineColor: palette.staffLineColorNumber,
      cursorColor: palette.playbackCursorNumber,
      // Initial sight-reading transposition; updated live via SetSightReadingTranspose.
      transposeSemitones,
      modeConversion,
      // Live finger numbers: the tables phrases are fingered with and the
      // initial density (updated live via SetSightReadingFingeringDisplay).
      fingering,
    };
    this.phraseManager = new PhraseManagerMuseflowRenderer(
      this,
      { width, height },
      phraseManagerConfig,
    );
    // Propagate the initial cursor-visibility toggle so cursors the manager
    // creates (now and during gameplay) start render-hidden when toggled off.
    this.phraseManager.cursorHidden = this.cursorHidden;
    // Compute the layout reference up-front. Mobile doesn't fire ResizeWindow
    // reliably (no real-window resize, and the React ResizeObserver may fire
    // before the scene's listener is registered), so we don't depend on it.
    this.phraseManager.onResize({ width, height });
    this.cameraController = new CameraController(this, {
      zoomEnabled: true,
      followXEnabled: mobile ?? false,
      followOffsetX: 0,
      minRelativeZoom: 0.5,
      maxRelativeZoom: 1.0,
      zoomTweenDuration: 500,
      zoomTweenEase: 'Power2',
    });
    this.adaptiveTempoController = new AdaptiveTempoController(
      this.eventStream?.timeKeeper.getBpm() ?? 72,
      this.onAdaptiveTempoDone,
      this,
      goalTempo,
      freePlayMode,
      40,
      300,
      ataEnabled,
    );
    Logger.debug('[zoom::init] loading scene with ', zoomLevel);
    this.setZoomLevel({ zoomLevel: zoomLevel ?? 0.9 });
    /*if (zoomLevel) {
      this.zoomLevel = Math.max(0.5, Math.min(zoomLevel, 1));
      this.cameras.main.zoom = this.zoomLevel;
    } else {
      this.zoomLevel = 0.9;
      this.cameras.main.zoom = this.zoomLevel;
    }*/
    //console.debug(this.cameras.main);
    //console.debug('[zoom::init] loading scene with ', zoomLevel, this.cameras.main.zoom);
    // Reset all per-session state — the scene instance is reused across
    // tier changes and re-starts, so class property initializers do NOT run
    // again. Every mutable field must be explicitly reset here.
    /*this.phrases = [];
    this.currentIdx = undefined;
    this.currentCursor = 0;*/
    this.measureCount = 0;
    this.activePhrase = 0;
    this.phraseRawByUuid = new Map();
    this.lastTickTime = undefined;
    this.lastMeasure = undefined;
    this.playbackStarted = false;
    this.shouldUpdate = false;
    this.pauseQueued = false;
    this.gameplayStarted = false;
    this.eventsQueue = [];
    this.freePlayMode = freePlayMode;
    this.nextPhraseStartTimer = undefined;
    this.nextAdaptiveSegment = undefined;
    this.startPlayingTriggerEventTimestamp = undefined;
    this.appTourStep = null;
    this.appTourPlaybackActive = false;
    this.savedTourState = undefined;
    this.demoTimeKeeper = undefined;
    this.appTourDemoIterator = undefined;
    this.appTourNoteIdsBySlice = [];
    this.appTourColoringStepIndex = 0;
    this.appTourDemoTouchedNoteIds = new Set();
    this.isTourActive = false;
    this.savedCursorPosition = undefined;

    // ATA triggers are driven by onFinishPlayingPhrase → AdaptiveTempoController (threshold-based),
    // not by chevron gain/loss, so no gain/loss callbacks are wired here.
    this.chevronManager = new ChevronManager(
      eventStream,
      this.tempo,
      72,
      4,
      undefined,
      undefined,
      this.freePlayMode,
    );
    this.errorFeedbackManager = new ErrorFeedbackManager(this);
    this.width = width;
    this.height = height;
    this.mobileView = mobile ?? false;
    this.timeKeeperAuxiliary = createTimeKeeper();
    this.eventStream = eventStream;
    // MUS-469: seed the eventStream with the scene's current mode (see scene
    // setFreePlayMode below). Telemetry only - does not affect gameplay.
    this.eventStream.freePlayMode = this.freePlayMode;
    this.eventStream.setErrorFeedbackManager(this.errorFeedbackManager, this);
    //this.phrases = [];
    const scorePalette = getScoreThemePalette(this.scoreTheme);
    this.cameras.main.setBackgroundColor(scorePalette.scoreBackgroundPhaser);
    // Default camera zoom origin is (0.5, 0.5): zoom scales around viewport center, so world (0,0)
    // is generally not the same as the top-left of what you see unless zoom is 1 and layout lines up.
    // For the world point at the viewport's top-left: getWorldPoint(0, 0) or worldView.x / worldView.y.
    //this.log = this.add.text(0, 0, "log");

    ReactToPhaserListeners.ResizeWindow(this.resize, this);
    ReactToPhaserListeners.PauseExercise(this.pauseExercise, this);
    ReactToPhaserListeners.MidiOn(this.noteOn, this);
    ReactToPhaserListeners.MidiOff(this.noteOff, this);
    ReactToPhaserListeners.SetZoomLevel(this.setZoomLevel, this);
    ReactToPhaserListeners.SetNoteState(this.setNoteState, this, false, false);
    ReactToPhaserListeners.PulseNote(this.pulseNote, this, false, false);
    ReactToPhaserListeners.StartExercise(this.startExercise, this, false, false);
    ReactToPhaserListeners.ChangeTempo(this.changeTempo, this, false, false);
    ReactToPhaserListeners.UpdateGoalTempo(this.updateGoalTempo, this, false, true);
    //ReactToPhaserListeners.AddPhrase(this.addPhrase, this, false, true);
    ReactToPhaserListeners.SetCursor(this.setCursor, this);
    ReactToPhaserListeners.SetFreePlayMode(this.setFreePlayMode, this, false, true);
    ReactToPhaserListeners.ResetNoteColor(this.resetNoteColor, this);
    ReactToPhaserListeners.ResetChevrons(() => {
      this.chevronManager?.reset();
    }, this);
    ReactToPhaserListeners.ClearPhraseQueue(({ playSessionId: newPlaySessionId }) => {
      // MUS-495: flush and refill are one operation. `clearPhraseQueue` empties
      // the queue on purpose, and refills are playback-driven — playback cannot
      // start from an empty queue, so a flush left un-primed is a blank score
      // and a dead play button, not a stale phrase. `restartForSession` does
      // both, and skips the pair outright when this manager was constructed for
      // the session that is being announced — a scene restart already filled it.
      if (!this.phraseManager?.restartForSession(newPlaySessionId)) return;
      // The rebuild destroyed the shared mobile cursor and the first-phrase
      // brace. `tryWireMobileCameraAnchor` wires each exactly once and only
      // while the slot is empty, so without dropping them here the camera keeps
      // following a destroyed cursor and mobile scroll-follow stops for the
      // rest of the tier. It runs every frame, so the replacements are picked
      // up as soon as the new tier's first phrase renders.
      this.cameraController?.setFollowTarget(null);
      this.cameraController?.clearLeftAnchor();
    }, this);
    ReactToPhaserListeners.SetNoteColor(this.setNoteColor, this);
    ReactToPhaserListeners.SetAppTourStep(this.setAppTourStep, this);
    ReactToPhaserListeners.EndTour(this.endTour, this);
    ReactToPhaserListeners.SetCursorVisible(this.setCursorVisible, this);
    // Guard against the 'click' key being missing from the audio cache. On a
    // hard reload the lesson scene can boot before Preloader has populated the
    // cache, and an unguarded `this.sound.add('click')` throws "Audio key
    // 'click' not found in cache" — which surfaces synchronously from the
    // scene-start effect and crashes the whole app. metronomeClick() lazily
    // creates the sound once the asset is available, so a miss here is silent,
    // not fatal.
    if (!isCapacitorNative() && !this.clickAudio) {
      if (this.cache.audio.exists('click')) {
        this.clickAudio = this.sound.add('click');
      } else {
        reportError(
          new Error(
            '[SightReadingPhaser] "click" audio not in cache at init; metronome lazy-loads on first beat',
          ),
          'audio.click',
        );
      }
    }
    ReactToPhaserListeners.AbortAdaptiveTempo(this.abortAdaptiveSegment, this);
    ReactToPhaserListeners.SetAtaEnabled(this.setAtaEnabled, this);
    ReactToPhaserListeners.SetSightReadingTranspose(this.setSightReadingTranspose, this);
    ReactToPhaserListeners.SetSightReadingFingeringDisplay(
      this.setSightReadingFingeringDisplay,
      this,
    );
    /*this.phrase1Gizmo = this.add.rectangle(0, 0, 100, 100, 0x00ff00, 0.5);
    this.phrase2Gizmo = this.add.rectangle(0, 0, 100, 100, 0x00ff00, 0.5);
    this.phrase1Gizmo?.setOrigin(0, 0);
    this.phrase2Gizmo?.setOrigin(0, 0);*/
    // Request-driven ingestion: the manager pulls phrases from React (via
    // onPhraseRequested -> FetchPhrase) until its visible queue is filled.
    //
    // MUS-495: record which session this fill belongs to, when the scene starts
    // with one in hand. React announces every committed session over
    // ClearPhraseQueue, and without this the announcement for the session we
    // just filled for would flush that fill and pay for it again. The producer
    // side declines to fetch when there is no session yet, so booting ahead of
    // session creation costs nothing and the announcement primes the queue.
    this.phraseManager.primedForSession = playSessionId;
    this.phraseManager.requestInitialPhrases();
  }

  onPhraseRequested(requestParams: RequestParams) {
    NotifyReactEvents.FetchPhrase(requestParams);
  }

  setAtaEnabled({ enabled }: { enabled: boolean }) {
    this.adaptiveTempoController?.setIsEnabled(enabled);
  }

  /** Live transpose: the manager re-transposes the on-screen phrases in place.
   *  Playback is reset to the top so the clock re-syncs on the next play
   *  (React pauses the lesson alongside this event). */
  setSightReadingTranspose({
    semitones,
    modeConversion,
  }: {
    semitones: number;
    modeConversion: ParallelModeConversion;
  }) {
    // Mobile uses one shared cursor as the camera follow target. setTranspose
    // destroys that cursor while rebuilding the visible phrases, so release
    // both camera references before the rebuild and wire them to the newly
    // created cursor/brace afterward. Otherwise the camera keeps following the
    // destroyed cursor and stops scrolling even though playback continues.
    if (this.mobileView) {
      this.cameraController.setFollowTarget(null);
      this.cameraController.clearLeftAnchor();
    }
    const rebuiltPhraseCount = this.phraseManager.setTranspose(semitones, modeConversion);
    if (this.mobileView) {
      this.tryWireMobileCameraAnchor();
    }
    NotifyReactEvents.SightReadingTransposeApplied({
      semitones,
      modeConversion,
      rebuiltPhraseCount,
    });
    this.playbackStarted = false;
    this.shouldUpdate = false;
    this.lastTickTime = undefined;
  }

  abortAdaptiveSegment({ tempoRestorePoint }: { tempoRestorePoint?: number }) {
    this.adaptiveTempoController?.abortAdaptiveSegment({ tempoRestorePoint });
    this.nextAdaptiveSegment = undefined;
  }

  setZoomLevel({ zoomLevel }: { zoomLevel: number }) {
    Logger.debug('[zoom::setZoomLevel] setting zoom level', zoomLevel);
    if (zoomLevel === undefined) {
      Logger.warn('[zoom::setZoomLevel] zoomLevel is undefined!');
      return;
    }
    const zoom = Math.max(0.5, Math.min(zoomLevel, 1));
    this.zoomLevel = zoom;
    this.phraseManager?.setRelativeZoom(zoom);
    this.refreshCameraMaxZoom();
    this.cameraController.zoomTo(zoom);
  }

  /**
   * Push the latest maxZoom to the controller (re-applies the current zoom
   * with the new ceiling). On mobile this also tries to wire up the follow
   * target + left anchor — see {@link tryWireMobileCameraAnchor}. Both are
   * idempotent, so it's safe to call repeatedly.
   */
  private refreshCameraMaxZoom(): void {
    if (!this.cameraController) return;
    this.cameraController.setBaselines({
      paddingLeft: this.cameraController.getPaddingLeft(),
      maxZoom: this.phraseManager.maxZoomLevel,
    });
    if (this.mobileView) this.tryWireMobileCameraAnchor();
  }

  /**
   * Mobile camera setup: hand the controller a follow target (the shared
   * cursor) and a left-edge anchor (the first-phrase brace). The controller
   * handles paddingLeft + scroll math from there. No-op once both are wired.
   */
  private tryWireMobileCameraAnchor(): void {
    if (!this.cameraController || !this.mobileView) return;
    if (this.mobileCursor && !this.cameraController.getFollowTarget()) {
      this.cameraController.setFollowTarget(this.mobileCursor);
    }
    if (!this.cameraController.hasLeftAnchor()) {
      const anchor = this.phraseManager.getMobileLeftAnchor();
      if (anchor) {
        this.cameraController.setLeftAnchor(anchor.worldX, anchor.screenGap);
      }
    }
  }

  setFreePlayMode({ enabled }: { enabled: boolean }) {
    console.debug('[collected message] setFreePlayMode', enabled);
    this.adaptiveTempoController?.setIsFreePlay(enabled);
    this.freePlayMode = enabled;
    this.chevronManager?.setFreePlayMode(enabled);
    // MUS-469: keep the eventStream's mode in sync so it is snapshotted onto
    // each note as it is graded. Telemetry only - does not affect gameplay.
    if (this.eventStream) this.eventStream.freePlayMode = enabled;
  }

  startExerciseDummy() {
    NotifyReactEvents.StartedPlaying();
  }

  setCursor(_props: { measure?: number }) {
    if (!this.playbackStarted) {
      return;
    }
    const cursor = this.mobileCursor ?? this.phrases[this.activePhrase]?.cursor;
    if (!cursor) console.error('[setCursor] No cursor found!', this.phrases, this.activePhrase);
    const measure = Math.max(cursor!.getLastPlayedMeasure() - 1, 0);
    if (measure === -1) return;
    const phraseIdx = this.phrases.findIndex((phrase) => phrase?.graphic.containsMeasure(measure));
    if (phraseIdx === undefined) {
      console.error("given measure doesn't appear in any of the active phrases!");
    } else {
      const phrase = this.phrases[phraseIdx];
      if (phrase !== this.phrases[this.activePhrase]) {
        console.debug(
          '[setCursor] phrase changed',
          phraseIdx,
          this.activePhrase,
          this.phrases,
          this.activePhrase,
        );
        const current = this.phrases[this.activePhrase]!;
        const firstNotePos = this.phrases[this.activePhrase]!.graphic.getFirstNotePosition();
        const cursor = this.mobileCursor ?? this.phrases[this.activePhrase]?.cursor;
        cursor?.setStart(firstNotePos, current.graphic._data.startTimestamp, 0);
        cursor?.setX(firstNotePos - cursor!.width * 2);
        current?.deactivate();
        this.activePhrase = phraseIdx;
      }
      phrase?.activate();
      const firstNote =
        phrase?.graphic.getFirstNotePositionInMeasure(
          measure - phrase.graphic.phraseStartingMeasure!,
        ) ?? 0;
      const cursorStartPos = firstNote!;
      const numMeasuresIn = measure - (phrase?.graphic.phraseStartingMeasure ?? 0);
      const timesig = phrase?.graphic._data.timeSignature ?? new TimeSignature(4, 4);
      const cursor = (this.mobileCursor ?? this.phrases[this.activePhrase]?.cursor)!;
      cursor.lastPlayedMeasure = measure - cursor.startingMeasure;
      cursor.setStart(
        cursorStartPos,
        (timesig.numerator / timesig.denominator) * numMeasuresIn,
        measure - cursor.startingMeasure,
      );
    }
  }

  changeTempo({ tempo }: { tempo: number }) {
    this.tempo = tempo;
    this.timeKeeperAuxiliary!.setBpm(tempo);

    // If waiting for the first note (cursor blinking), update blink speed
    // to match the new tempo so the user sees the change immediately.
    if (!this.playbackStarted) {
      const activePhrase = this.phrases[this.activePhrase];
      const cursor = this.mobileCursor ?? activePhrase?.cursor;
      cursor?.blink((60 / tempo) * 1000);
    }
  }

  setSightReadingFingeringDisplay({ display }: { display: FingeringDisplay }) {
    this.phraseManager?.setFingeringDisplay(display);
  }

  setCursorVisible = ({ visible }: { visible: boolean }): void => {
    this.cursorHidden = !visible;
    // Keep the manager in sync so phrases created later (during gameplay/scroll)
    // also honor the current toggle.
    if (this.phraseManager) {
      this.phraseManager.cursorHidden = !visible;
    }
    if (this.mobileCursor) {
      this.mobileCursor.setCursorVisibility(visible);
    }
    for (const phrase of this.phrases) {
      phrase?.cursor?.setCursorVisibility(visible);
    }
  };

  metronomeClick() {
    notifyAppAudioOutput({ reason: 'metronome' });
    if (isCapacitorNative()) {
      MuseFlowAudio.playSoundEffect({ name: 'click' });
    } else {
      // Lazily create the click sound now that the asset may have finished
      // loading (init() can run before Preloader populates the cache).
      if (this.clickAudio === undefined && this.cache.audio.exists('click')) {
        this.clickAudio = this.sound.add('click');
      }
      if (this.clickAudio === undefined) {
        Logger.warn('metronome audio not loaded!');
      }
      this.clickAudio?.stop();
      this.clickAudio?.play();
    }
  }

  startExercise({ tempo }: { tempo?: number }) {
    if (tempo) {
      this.tempo = tempo;
    }
    if (AUTOSTART && process.env.REACT_APP_NODE_ENV === 'local') {
      setTimeout(() => this.noteOn({ note: 60, velocity: 1 }), 500);
    }
    if (!this.phrases[0]) {
      console.warn('[SightReadingPhaser] startExercise called before phrases loaded — deferring');
      return;
    }
    this.shouldUpdate = true;
    if (!this.playbackStarted) {
      const secondsPerMeasure = (60 / this.tempo) * 4;
      const measureOffset = TimeKeeper.METRONOME_SCHEDULE_AHEAD_TIME / secondsPerMeasure;
      if (!isCapacitorNative()) {
        Tone.start();
      }
      this.timeKeeperAuxiliary!.setTimeSignatures(0, [new TimeSignature(4, 4)]);
      this.timeKeeperAuxiliary!.resetTime();
      this.timeKeeperAuxiliary!.unpause(this.tempo, 0, measureOffset, NoteType._32nd);
      this.phrases[0]!.activate();
      this.mobileCursor?.enable();
      this.mobileCursor?.unpause();
      const cursor = this.mobileCursor ?? this.phrases[0]!.cursor;
      console.debug('[startExercise] cursor=', cursor);
      cursor?.blink((60 / this.tempo) * 1000);
    } else {
      console.log('[startExercise] === RESUME START ===');
      console.log('[startExercise] activePhrase=', this.activePhrase);
      console.log(
        '[startExercise] phrase slots:',
        this.phrases.map((p, i) =>
          p
            ? `[${i}] uuid=${p.graphic._data.uuid} ts=${p.graphic._data.startTimestamp} active=${p.isActive} destroyed=${p.graphic.isDestroyed}`
            : `[${i}] empty`,
        ),
      );
      console.log(
        '[startExercise] activePhrase exists=',
        !!this.phrases[this.activePhrase],
        'cursor exists=',
        !!this.phrases[this.activePhrase]?.cursor,
      );
      NotifyReactEvents.StartedPlaying();
      const cursor = this.mobileCursor ?? this.phrases[this.activePhrase]?.cursor;
      this.phrases[this.activePhrase]?.activate();
      cursor?.unpause();
      console.log('[startExercise] === RESUME END ===');
    }
  }

  // runs every 'tick'
  tickUpdate(beatDuration: number) {
    this.tempo = this.eventStream?.timeKeeper.getBpm() ?? 72;
    if (this.appTourPlaybackActive) {
      this.metronomeClick();
      return;
    }
    if (this.playbackStarted) {
      this.chevronManager?.onTick(this.tempo);
    } else {
      this.metronomeClick();
      this.mobileCursor?.blink(beatDuration);
      this.phrases[this.activePhrase]?.cursor?.blink(beatDuration);
    }
    const currentTimestamp = this.eventStream?.timeKeeper?.audioTimeToMeasureTimestamp() ?? 0;
    //this.debugLog(`[tickUpdate] currentTimestamp=${currentTimestamp}`);
    if (this.adaptiveTempoController) {
      const newTempo = this.adaptiveTempoController?.onTick(currentTimestamp);
      if (newTempo) {
        this.debugLog(`newTempo=${newTempo}`);
        //this.tempo = newTempo;
        //this.timeKeeperAuxiliary!.setBpm(newTempo);
      }
    }
  }

  update() {
    if (this.pauseQueued) {
      this._pauseExercise();
    }
    // Wire up the mobile camera anchor once both the cursor and first-phrase
    // brace exist. After that, the controller owns paddingLeft and all
    // scroll/zoom math — the scene doesn't touch the camera again. The
    // controller's follow runs on the scene's `prerender` event (after game
    // objects move but before the camera matrix is built), so the scene
    // doesn't need to call its `update()` here.
    this.tryWireMobileCameraAnchor();
    const cam = this.cameras.main;
    const cursor = this.mobileCursor;
    const cursorX = cursor?.x;
    const cursorPhraseIdx = cursor?.getEffectivePhraseIndex();
    const visiblePhrases = this.phrases.filter((p) => p !== undefined).length;
    this.debugLog(
      `cam x=${cam.scrollX.toFixed(1)} y=${cam.scrollY.toFixed(1)} zoom=${cam.zoom.toFixed(3)}\n` +
        `cursor x=${cursorX !== undefined ? cursorX.toFixed(1) : '-'} phraseIdx=${cursorPhraseIdx ?? '-'} phrases=${visiblePhrases}`,
    );
    if (this.shouldUpdate) {
      this.mobileCursor?.update();
      this.phrases.forEach((p) => p?.update());

      // App tour playback: end demo when cursor reaches end of (top) phrase
      const topPhraseForTour = this.getTopPhrase();
      if (this.appTourPlaybackActive && topPhraseForTour?.cursor && this.appTourDemoIterator) {
        const topCursor = topPhraseForTour.cursor;
        if (topCursor.isDemoMode() && this.appTourDemoIterator.EndReached) {
          this.demoTimeKeeper?.pause();
          this.demoTimeKeeper?.resetTime();
          topCursor.clearDemoOverride();
          this.appTourPlaybackActive = false;
          this.appTourDemoIterator = undefined;
        }
      }

      const timeKeeper =
        this.appTourPlaybackActive && this.demoTimeKeeper
          ? this.demoTimeKeeper
          : this.playbackStarted
            ? this.eventStream?.timeKeeper
            : this.timeKeeperAuxiliary;
      if (timeKeeper === undefined) {
        Logger.warn('timekeeper unavailable!');
        return;
      }
      const timestamp = timeKeeper.audioTimeToMeasureTimestamp();
      if (this.lastTickTime === undefined) {
        this.lastTickTime = timestamp - TimeKeeper.METRONOME_SCHEDULE_AHEAD_TIME;
      }
      const beatFraction = 1 / 4;
      const measureDurationSinceLastTickTime = timestamp - this.lastTickTime;
      const bpmForBeat = this.appTourPlaybackActive ? 72 : (this.tempo ?? 72);
      if (measureDurationSinceLastTickTime > beatFraction) {
        const beatDuration = (60 / bpmForBeat) * 1000;
        this.tickUpdate(beatDuration);
        this.lastTickTime =
          this.lastTickTime === undefined
            ? undefined
            : timestamp - (timestamp - this.lastTickTime - 0.25);
      }
    }
  }

  updateGoalTempo({ goalTempo }: { goalTempo: number }) {
    console.debug('[collected message] updateGoalTempo', goalTempo);
    this.adaptiveTempoController?.setGoalTempo(goalTempo);
    this.chevronManager?.setGoalTempo(goalTempo);
  }

  showToast(message: string) {
    Logger.debug(message);
  }

  onPlayingLastSlice(start: number, duration: number, _durationInSecs: number) {
    if (!this.mobileView) {
      const phrase = this.phraseManager.getNextPhrase();
      if (phrase) {
        const firstNotePos = phrase.graphic.getFirstNotePosition();
        phrase.cursor?.setX(firstNotePos - (phrase.cursor?.width ?? 0));
        phrase.activate(false, false);
        phrase.cursor?.startCountingIn(start, duration, duration);
      }
    }
  }

  onFinishPlayingPhrase(playedOnce?: boolean) {
    const eventStream = this.eventStream;
    const acc = eventStream != null ? eventStream.calcAccuracy() : 0;
    const nextPhrase = this.phraseManager.getNextPhrase();
    const activePhrase = this.phrases[this.activePhrase];
    //if (!this.freePlayMode) {
    this.chevronManager?.onPhraseFinishPlaying(playedOnce ?? false, nextPhrase, activePhrase);
    //}
    // Forward the finished phrase (uuid + accuracy) to the ATA controller, which decides whether to
    // adjust tempo: +ve delta speeds up, -ve slows down, undefined means no change. The controller
    // owns the customisable thresholds, so this is no longer tied to chevron gain/loss.
    const finishedUuid = activePhrase?.graphic._data.uuid;
    if (finishedUuid != null && nextPhrase && activePhrase) {
      const delta = this.adaptiveTempoController?.checkAtaTriggerOnPhraseEnd(finishedUuid, acc);
      if (delta != null && delta !== 0) {
        this.triggerAdaptiveSegment(nextPhrase, activePhrase, delta);
      }
    }
    const nextSlot = this.phraseManager.getNextIndex();
    if (!this.mobileView) {
      this.phrases[this.activePhrase]?.deactivate();
      this.phrases[nextSlot]?.activate(true);
      this.phrases[nextSlot]?.cursor?.setEligible(acc >= 95);
    }
    this.activePhrase = nextSlot;
  }

  /**
   * Set up the next adaptive tempo segment on the upcoming phrase. `delta` is the signed tempo
   * change decided by the ATA controller (+ve speeds up, -ve slows down). Respects per-direction
   * tempo limits via shouldStartAdaptiveSegment and skips if the active phrase already has a segment.
   */
  triggerAdaptiveSegment(nextPhrase: PlayablePhrase, activePhrase: PlayablePhrase, delta: number) {
    if (activePhrase.graphic.adaptiveSegmentState) return;
    if (!this.adaptiveTempoController?.shouldStartAdaptiveSegment(delta, this.tempo)) return;
    const direction = delta > 0 ? 'speed-up' : 'slow-down';
    const timeSig = nextPhrase.graphic._data.timeSignature;
    const beatsPerMeasure = timeSig?.numerator ?? 4;
    const measureDuration = (1 / 4) * beatsPerMeasure;
    const measureStartTimestamp = nextPhrase.graphic._data.startTimestamp + measureDuration;
    const measureEndTimestamp = measureStartTimestamp + measureDuration * 2;
    nextPhrase.graphic.setAdaptiveSegment(1, 2, direction);
    NotifyReactEvents.AdaptiveTempoRegionCreated(direction);
    console.debug('[AtaMessage] regionCreated', direction, delta);
    this.nextAdaptiveSegment = {
      base: this.tempo,
      delta,
      startTimestamp: measureStartTimestamp,
      endTimestamp: measureEndTimestamp,
    };
  }

  onFinishAdaptiveSegment(finalTempo: number) {
    this.tempo = finalTempo;
    this.nextAdaptiveSegment = undefined;
  }

  onStartPlayingMeasure(globalMeasure: number, phraseLocalMeasure: number) {
    console.debug(
      '[ata] global=',
      globalMeasure,
      'phraseLocal=',
      phraseLocalMeasure,
      this.nextAdaptiveSegment,
    );
    const isBottomPhrase = this.phrases[this.activePhrase]?.graphic.bottom || false;
    // ATA segments are anchored to phrase-local measures (so the segment kicks
    // in at the same point of every phrase regardless of cumulative measure).
    if (phraseLocalMeasure === 1 && this.nextAdaptiveSegment) {
      this.adaptiveTempoController?.startAdaptiveSegment(
        this.nextAdaptiveSegment.base,
        this.nextAdaptiveSegment.delta,
        this.nextAdaptiveSegment.startTimestamp,
        this.nextAdaptiveSegment.endTimestamp,
        isBottomPhrase,
      );
    } else if (phraseLocalMeasure === 3 && this.nextAdaptiveSegment) {
      //this.adaptiveTempoController?.finishAdaptiveSegment();
      //this.nextAdaptiveSegment = undefined;
    }
  }

  getNoteById(id: string, phraseStart?: number): IPlayable | null {
    const note = this.phrases.map((phrase) => ({
      note: phrase?.graphic.getNoteById(id),
      phraseStart,
    }));
    note.forEach((n) => {
      if (n.note !== undefined && n.phraseStart !== phraseStart) {
        // intentionally empty
      }
    });
    return note.map((n) => n.note).find((n) => n !== undefined) ?? null;
  }

  // The update function might still be running when the Puase callback is triggered by
  // the registered event which will mess up the validity of the state.
  // Therefore, we queue a pause request which then immediately gets processed
  // in the next update() tick.
  pauseExercise() {
    this.pauseQueued = true;
  }

  _pauseExercise() {
    this.shouldUpdate = false;
    this.lastTickTime = undefined;
    this.phrases[this.activePhrase]?.pause();
    this.mobileCursor?.pause();
    const activePhrase = this.phrases[this.activePhrase];
    const cursor = activePhrase?.cursor ?? this.mobileCursor;
    const lastPlayedMeasure = cursor ? Math.max(cursor.getLastPlayedMeasure() - 1, 0) : 0;
    console.debug('[pauseExercise] lastPlayedMeasure=', lastPlayedMeasure);
    NotifyReactEvents.ExercisePaused({ lastPlayedMeasure });
    this.pauseQueued = false;
    this.setCursor({});
  }

  getCurrentPhrase() {
    return this.phrases.find((phrase) => phrase?.isActive)?.graphic ?? null;
  }

  setNoteState({
    id,
    state,
    colorblindType,
    noteTimestamp,
  }: {
    id: string;
    state: ProgressState;
    colorblindType?: COLORBLIND_TYPE | null;
    noteTimestamp?: number;
  }) {
    const note = this.getNoteByIdAndTimestamp(id, noteTimestamp);
    if (!note) {
      Logger.warn(
        `[setNoteState] id=${id} state=${state} noteTimestamp=${noteTimestamp} NOT FOUND in any phrase`,
      );
    }
    note?.setTimingState(state, colorblindType);
  }

  pulseNote({
    id,
    colorblindType,
    noteTimestamp,
  }: {
    id: string;
    colorblindType?: COLORBLIND_TYPE | null;
    noteTimestamp?: number;
  }) {
    this.getNoteByIdAndTimestamp(id, noteTimestamp)?.pulse(colorblindType);
  }

  getNoteByIdAndTimestamp(id: string, timestamp?: number): IPlayable | null {
    for (let i = 0; i < this.phrases.length; i++) {
      const phrase = this.phrases[i];
      if (!phrase) continue;
      const note = phrase.graphic.getNoteById(id, timestamp);
      if (note != null) {
        return note as IPlayable;
      }
    }
    // Log diagnostic info when note is NOT found
    console.warn(`[getNoteByIdAndTimestamp] NOT FOUND id=${id} ts=${timestamp}. Active slots:`);
    for (let i = 0; i < this.phrases.length; i++) {
      const phrase = this.phrases[i];
      if (!phrase) {
        console.warn(`  slot ${i}: empty`);
        continue;
      }
      const noteCount =
        (phrase.graphic.trebleClef?.notes.size ?? 0) + (phrase.graphic.bassClef?.notes.size ?? 0);
      const sampleIds: string[] = [];
      phrase.graphic.trebleClef?.notes.forEach((n) => {
        if (sampleIds.length < 3) sampleIds.push(n.id);
      });
      phrase.graphic.bassClef?.notes.forEach((n) => {
        if (sampleIds.length < 5) sampleIds.push(n.id);
      });
      console.warn(
        `  slot ${i}: uuid=${phrase.graphic._data.uuid} startTs=${
          phrase.graphic._data.startTimestamp
        } destroyed=${
          phrase.graphic.isDestroyed
        } noteCount=${noteCount} sampleIds=[${sampleIds.join(',')}]`,
      );
    }
    return null;
  }

  getNotePosition(id: string, timestamp?: number, rest?: boolean) {
    const pos =
      this.phrases
        .map((p) => {
          return p?.graphic.getNotePosition(id, timestamp, rest);
        })
        .find((p) => p !== undefined) ?? null;
    if (pos !== null) {
      return pos.x;
    }
    return null;
  }

  getNoteFullPosition(id: string, timestamp?: number, rest?: boolean) {
    return (
      this.phrases
        .map((p) => {
          return p?.graphic.getNotePosition(id, timestamp, rest);
        })
        .find((p) => p !== undefined) ?? null
    );
  }

  getPhraseEndPosition() {
    return Math.max(
      ...this.phrases
        .filter((p) => p !== undefined)
        .map((phrase) => phrase.graphic.getNextPosition()),
    );
  }

  setNoteColor({ color, id }: { color: string | number; id?: string }) {
    console.log(`[setNoteColor] color=${color} id=${id}`);
    if (id === undefined) {
      const phrase = this.getCurrentPhrase() ?? this.phrases.find((p) => p !== undefined)?.graphic;
      const firstNoteId = phrase?.getFirstNoteId();
      if (firstNoteId) phrase?.getNoteById(firstNoteId)?.setColor(color);
    } else {
      this.getNoteById(id)?.setColor(color);
    }
  }

  resetNoteColor({ id }: { id?: string }) {
    if (id === undefined) {
      const phrase = this.getCurrentPhrase() ?? this.phrases.find((p) => p !== undefined)?.graphic;
      const firstNoteId = phrase?.getFirstNoteId();
      if (firstNoteId) phrase?.getNoteById(firstNoteId)?.resetColor();
    } else {
      //this.getNoteById(id)?.resetColor();
    }
  }

  /**
   * Get the current cursor position and active phrase.
   * Used by error feedback system to spawn visuals at cursor location.
   */
  getCursorPosition(): { x: number; phrase: PlayablePhrase } | null {
    const activePhrase = this.phrases[this.activePhrase];
    if (!activePhrase || !activePhrase.cursor) {
      return null;
    }
    return {
      x: activePhrase.cursor.x,
      phrase: activePhrase,
    };
  }

  startPlaying() {
    this.timeKeeperAuxiliary?.pause();
    this.timeKeeperAuxiliary?.resetTime();
    this.phrases[0]?.activate();
    this.mobileCursor?.enable();
    this.phrases[0]?.cursor?.unpause(0);
    NotifyReactEvents.StartedPlaying();
    this.playbackStarted = true;
  }

  noteOn(payload: MidiOnPayload) {
    const { note, source, inputLatencyMs, observedAtMs } = payload;
    if (this.playbackStarted && !this.gameplayStarted) {
      this.eventsQueue.push(payload);
    }
    if (!this.playbackStarted && this.eventStream !== undefined) {
      const rawTimestamp = this.timeKeeperAuxiliary!.audioTimeToMeasureTimestamp();
      const timestamp =
        source === 'audio-recognition'
          ? getLatencyAdjustedMeasureTimestamp(
              rawTimestamp,
              this.timeKeeperAuxiliary,
              getEventInputLatencyMs(inputLatencyMs ?? 0, { source, observedAtMs }),
            )
          : rawTimestamp;
      if (!this.lastTickTime) {
        return;
      }
      const timeDiff = timestamp - this.lastTickTime;
      if (timeDiff > 0.06 && timeDiff < 0.125) {
        //this.showToast(`Too Late`);
        console.debug('[noteOn] Too late');
        NotifyReactEvents.ShowToast('Too late');
      } else if (timeDiff >= 0.125 && timeDiff < 0.19) {
        //this.showToast(`Too early`);
        NotifyReactEvents.ShowToast('Too early');
        console.debug('[noteOn] Too early');
        //} else if (!this.eventStream.isPitchValidForSightReadingStart(note, timestamp)) {
      } else if (!this.checkFirstNotePitch(note)) {
        console.debug('[noteOn] Wrong pitch!');
        //this.phrases[0]?.graphic.shakeFirstNote();
        NotifyReactEvents.ShowToast('Wrong pitch!');
        // On-beat but wrong pitch — do not start playback (same as off-time; avoids red / WRONG_NOTE start).
      } else {
        console.debug('[noteOn] Starting playback!');
        this.timeKeeperAuxiliary?.pause();
        this.timeKeeperAuxiliary?.resetTime();
        const measureOffset = timeDiff > 0.125 ? timeDiff - 0.25 : timeDiff;
        this.phrases[0]?.activate();
        const cursor = this.mobileCursor ?? this.phrases[0]?.cursor;
        cursor?.enable();
        cursor?.unpause(measureOffset);
        this.eventsQueue.push(payload);
        NotifyReactEvents.StartedPlaying({
          measureOffset,
          processEventsQueue: [this.processEventsQueue, this],
        });
        this.playbackStarted = true;
      }
    }
  }

  processEventsQueue() {
    this.eventsQueue.forEach(({ note, velocity, source, observedAtMs }) => {
      if (velocity === 0) this.eventStream?.handleNoteOffEvent(note, { source, observedAtMs });
      else this.eventStream?.handleNoteOnEvent(note, velocity, { source, observedAtMs });
    });
    this.eventsQueue = [];
  }

  checkFirstNotePitch(note: number) {
    console.debug('[noteOn::checkFirstNotePitch] Checking first note pitch!', { note });
    const cursor = this.mobileCursor ?? this.phrases[0]?.cursor;
    if (!cursor) return false;
    const hasNote = cursor.cursorData[0].currentVoiceEntries.some((chord) =>
      chord.Notes.some((n) => !n.isRest && midiNumberFromOsmdPitch(n.pitch) === note),
    );
    return hasNote;
  }

  noteOff({ note: _note }: { note: number }) {}

  destroy() {
    // Clean up error feedback for all phrases
    this.errorFeedbackManager?.cleanupAll();
    this.cameraController?.destroy();
    // The manager owns phrase + cursor teardown and its own listeners.
    this.phraseManager?.destroy();
    RemoveListener('SetCursor');
    RemoveListener('UpdateGoalTempo');
    RemoveListener('ResizeWindow');
    RemoveListener('SetFreePlayMode');
    RemoveListener('SetZoomLevel');
    RemoveListener('PauseExercise');
    RemoveListener('StartExercise');
    RemoveListener('ChangeTempo');
    RemoveListener('SetNoteState');
    RemoveListener('PulseNote');
    RemoveListener('SetCursorVisible');
    RemoveListener('UnloadExercise');
    RemoveListener('MidiOn');
    RemoveListener('MidiOff');
    RemoveListener('SetFreePlayMode');
    RemoveListener('ResetChevrons');
    RemoveListener('ClearPhraseQueue');
    RemoveListener('SetNoteColor');
    RemoveListener('ResetNoteColor');
    RemoveListener('SetAppTourStep');
    RemoveListener('EndTour');
    RemoveListener('AbortAdaptiveTempo', this.abortAdaptiveSegment);
    RemoveListener('SetAtaEnabled', this.setAtaEnabled);
    RemoveListener('SetSightReadingTranspose');
    RemoveListener('SetSightReadingFingeringDisplay');
    this.playbackStarted = false;
    this.shouldUpdate = false;
    this.pauseQueued = false;
  }

  resize(props: { width: number; height: number }) {
    this.transformPointToScreen(0, 0);
    const width = props.width;
    const height = props.height;
    this.width = width;
    this.height = height;
    this.scale.resize(width * window.devicePixelRatio, height * window.devicePixelRatio);
    // Execution order matters: the manager lays out the phrases AND recomputes
    // the zoom ceiling (updateMaxZoomLevel) inside onResize first; only then do
    // we re-apply the zoom tween using that fresh ceiling.
    this.phraseManager.resize({ width, height });
    if (!this.mobileView) {
      this.setZoomLevel({ zoomLevel: this.zoomLevel });
    }
  }

  /**
   * Get note IDs in order by vertical slice (notes only, no rests).
   * One ID per slice: the first non-rest note in that slice.
   */
  private getNoteIdsBySlice(phrase: PlayablePhrase): string[] {
    const data = phrase.graphic._data;
    const ids: string[] = [];
    for (const slice of data.serializedIterator) {
      let found = false;
      for (const entry of slice.currentVoiceEntries) {
        for (const note of entry.Notes) {
          if (note.isRest || !note.printObject) continue;
          const id = note.graphics?.[0]?.staveNote?.id;
          if (id) {
            ids.push(id);
            found = true;
            break;
          }
        }
        if (found) break;
      }
    }
    return ids;
  }

  private onAdaptiveTempoDone(finalTempo: number) {
    this.phrases[this.activePhrase]?.graphic.doneAdaptiveSegment();
    this.onFinishAdaptiveSegment(finalTempo);
  }

  setAppTourStep({ step, cbType }: { step: AppTourStepPhaser; cbType: COLORBLIND_TYPE | null }) {
    if (!this.isTourActive) {
      const topPhrase = this.getTopPhrase();
      const cursor = topPhrase?.cursor ?? this.mobileCursor;
      if (cursor) this.savedCursorPosition = cursor.x;
      console.debug('[tour] setAppTourStep', topPhrase, this.savedCursorPosition);
      //topPhrase.cursor.savePosition();
      this.isTourActive = true;
    }
    this.appTourStep = step;

    if (step === 'playback') {
      //this.startAppTourPlayback();
      return;
    }
    if (step === 'perfect-notes' || step === 'off-time-notes' || step === 'error-notes') {
      this.applyAppTourColoringStep(step, cbType);
      return;
    }
  }

  private restoreCursor() {
    const phrase = this.getTopPhrase();
    if (!phrase?.cursor) return;
    //phrase.cursor.setX(phrase.cursor.lastNotePos);
    //phrase.cursor.setY(phrase.cursor.y);

    //phrase.cursor.setWidth(phrase.cursor.width);
    //phrase.cursor.setHeight(phrase.cursor.height);
  }

  private startAppTourPlayback() {
    const top = this.getTopPhrase();
    if (!top?.cursor) {
      Logger.warn('[AppTour] Playback step requires at least the top phrase on screen');
      return;
    }

    // 1. Save game state for restore on EndTour
    this.savedTourState = {
      activePhrase: this.activePhrase,
      timeKeeperPaused: this.eventStream?.timeKeeper?.isPaused() ?? true,
      lastTickTime: this.lastTickTime,
    };

    // 2. Normalize top phrase cursor data so first slice has currentTimestamp 0 (align with metronome)
    const topCursorData = top.cursor.cursorData;
    const firstTimestamp = topCursorData.length > 0 ? topCursorData[0].currentTimestamp : 0;
    const normalizedCursorData = topCursorData
      .map((item) => ({
        ...item,
        currentVoiceEntries: item.currentVoiceEntries.filter(
          (e) => e.Notes.filter((n) => n.printObject).length > 0,
        ),
        currentTimestamp: item.currentTimestamp - firstTimestamp,
      }))
      .filter((s) => s.currentVoiceEntries.length > 0);

    // 3. Demo TimeKeeper at 72 BPM; startMeasure 0 so timestamp 0 = first beat (cursor and clicks in sync)
    this.demoTimeKeeper = new TimeKeeper();
    const timeSig = top.graphic._data.timeSignature;
    this.demoTimeKeeper.setTimeSignatures(0, [timeSig]);
    this.demoTimeKeeper.setBpm(72);
    this.demoTimeKeeper.resetTime();
    Tone.start();
    this.demoTimeKeeper.unpause(72, 0, 0, NoteType._32nd);

    // 4. Demo iterator (top phrase only, timestamps start at 0)
    const topPhraseRaw = this.phraseRawByUuid.get(top.graphic._data.uuid);
    if (!topPhraseRaw) {
      Logger.warn('[AppTour] Could not get phraseRaw for top phrase');
      return;
    }
    const demoIterator = new IteratorExtensible(
      this,
      normalizedCursorData,
      topPhraseRaw,
      timeSig,
      0,
    );
    this.appTourDemoIterator = demoIterator;

    // 5–6. Snap cursor to first note, attach demo (cursor uses existing update path driven by demo timekeeper)
    const firstNotePos = top.graphic.getFirstNotePosition();
    top.activate(false, true);
    top.cursor.setDemoOverride(this.demoTimeKeeper, demoIterator);
    top.cursor.setDemoStartPosition(firstNotePos);
    top.cursor.unpause();
    this.appTourPlaybackActive = true;
    if (!this.shouldUpdate) this.shouldUpdate = true;
  }

  private transformPointToScreen(x: number, y: number, scrollXOverride?: number) {
    const cam = this.cameras.main;
    const zoom = cam.zoom;
    const width = cam.width;
    const height = cam.height;
    const halfWidth = width / 2;
    const halfHeight = height / 2;
    // Subtract camera scroll so the projection stays correct when the
    // viewport pans (mobile scrolls the camera to follow the cursor).
    const scrollX = scrollXOverride ?? cam.scrollX;
    const xTransformed = (x - scrollX - halfWidth) * zoom;
    const yTransformed = (y - cam.scrollY - halfHeight) * zoom;
    const xTransformedTranslated = (xTransformed + halfWidth) / window.devicePixelRatio;
    const yTransformedTranslated = (yTransformed + halfHeight) / window.devicePixelRatio;
    return { x: xTransformedTranslated, y: yTransformedTranslated };
  }

  private applyAppTourColoringStep(
    step: 'perfect-notes' | 'off-time-notes' | 'error-notes',
    cbType: COLORBLIND_TYPE | null,
  ) {
    const phrase = this.getTopPhrase();
    const cursor = phrase?.cursor ?? this.mobileCursor;
    if (!cursor || !phrase?.graphic) return;
    cursor.clearDemoOverride();
    //if (step === 'perfect-notes') phrase.cursor.savePosition();
    if (step === 'perfect-notes') cursor.moveTo(this.savedCursorPosition ?? 0, true);
    if (this.appTourNoteIdsBySlice.length === 0) {
      this.appTourNoteIdsBySlice = this.getNoteIdsBySlice(phrase);
    }
    const noteIds = this.appTourNoteIdsBySlice;
    if (noteIds.length === 0) return;

    const stepIndex = step === 'perfect-notes' ? 0 : step === 'off-time-notes' ? 1 : 2;
    const prevStepIndex = this.appTourColoringStepIndex;
    this.appTourColoringStepIndex = stepIndex;

    // When going back a step, restore current note only
    if (stepIndex < prevStepIndex && prevStepIndex < noteIds.length) {
      const currentNoteId = noteIds[prevStepIndex];
      const note = this.getNoteById(currentNoteId) as NoteGraphic | null;
      note?.restoreFromDemo?.();
      this.appTourDemoTouchedNoteIds.delete(currentNoteId);
    }

    // Cursor to first note of first phrase
    //const firstNotePos = phrase.graphic.getFirstNotePosition();
    //phrase.cursor.targetNoteSmooth(firstNotePos - phrase.cursor.width / 2);
    //phrase.cursor.setX(firstNotePos - (phrase.cursor.width / 2));
    phrase.activate(false, true);

    const targetNoteId = noteIds[stepIndex];
    if (!targetNoteId) return;
    const note = this.getNoteById(targetNoteId) as NoteGraphic | null;
    if (!note) return;

    const targetPos = this.getNoteFullPosition(targetNoteId);
    const targetX = this.getNotePosition(targetNoteId);

    // On mobile, targetNoteSmooth animates scrollX over 300ms to follow the
    // cursor to the target note. We must send the dummy position *before* the
    // tween so the MUI Popper picks it up during the React re-render. The
    // CameraController follows the cursor by holding `scrollX = cursor.x -
    // paddingLeft`, so we can predict the final scrollX directly from the
    // post-tween cursor x (which targetNoteSmooth puts at `targetX - width/2`).
    if (targetPos !== null) {
      let predictedScrollX: number | undefined;
      if (
        this.mobileView &&
        this.mobileCursor &&
        targetX != null &&
        this.cameraController?.hasLeftAnchor()
      ) {
        const paddingLeft = this.cameraController.getPaddingLeft();
        predictedScrollX = targetX - this.mobileCursor.width / 2 - paddingLeft;
      }
      const transformed = this.transformPointToScreen(targetPos.x, targetPos.y, predictedScrollX);
      NotifyReactEvents.SetNotePosDummy(transformed);
    }

    if (targetX != null) {
      cursor.targetNoteSmooth(targetX);
    }

    const state =
      step === 'perfect-notes'
        ? ProgressState.Ended
        : step === 'off-time-notes'
          ? ProgressState.LateStart
          : ProgressState.Missed;
    note.setDemoTimingState?.(state, cbType);
    this.appTourDemoTouchedNoteIds.add(targetNoteId);
  }

  endTour() {
    this.isTourActive = false;

    console.debug('[tour] endTour', this.savedCursorPosition);
    const topPhrase = this.getTopPhrase();
    const cursor = topPhrase?.cursor ?? this.mobileCursor;
    //topPhrase?.cursor?.targetNoteSmooth(this.savedCursorPosition ?? 0);
    if (this.savedCursorPosition !== undefined) {
      //topPhrase?.cursor?.movementTweenHandle?.stop();
      const cursor = topPhrase?.cursor ?? this.mobileCursor;
      cursor?.moveTo(this.savedCursorPosition, true);
    }
    //topPhrase?.cursor?.targetNoteSmooth(this.savedCursorPosition ?? 0);
    this.savedCursorPosition = undefined;

    if (cursor) cursor.restorePosition();

    if (this.appTourPlaybackActive) {
      cursor?.clearDemoOverride();
      cursor?.pause();
      this.demoTimeKeeper?.pause();
      this.demoTimeKeeper?.resetTime();
      this.appTourPlaybackActive = false;
      topPhrase?.deactivate();
    }
    this.demoTimeKeeper = undefined;
    this.appTourDemoIterator = undefined;

    for (const noteId of this.appTourDemoTouchedNoteIds) {
      const note = this.getNoteById(noteId) as NoteGraphic | null;
      if (note?.restoreFromDemo) {
        note.restoreFromDemo();
      }
    }
    this.appTourDemoTouchedNoteIds.clear();

    if (this.savedTourState) {
      this.activePhrase = this.savedTourState.activePhrase;
      this.lastTickTime = this.savedTourState.lastTickTime;
      if (this.eventStream?.timeKeeper && this.savedTourState.timeKeeperPaused) {
        this.eventStream.timeKeeper.pause();
      }
      const restoredPhrase = this.phrases[this.activePhrase];
      if (restoredPhrase) {
        restoredPhrase.activate(false, true);
      }
      this.savedTourState = undefined;
    }

    this.appTourStep = null;
    this.appTourNoteIdsBySlice = [];
    this.appTourColoringStepIndex = 0;
  }
}
